import { useState } from "react";

import {
  DefaultButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  ProsDatabaseCommands,
  SiteComponentType
} from "@libs/api/gateways/sia/sia-ops-gateway.dtos";
import {
  useModifyDatabaseMutation,
  usePingMutation
} from "@libs/api/gateways/sia/sia-ops-gateway.hooks";

import {
  describeError,
  isOkResult,
  runInBatches,
  RunResult
} from "./deploy-management.utils";
import { ResultColumn } from "./ResultColumn";

interface Props {
  selectedTenants: Tenant[];
}

export const DeployManagementActions: React.FC<Props> = ({
  selectedTenants
}) => {
  const theme = useTheme();
  const modifyDatabase = useModifyDatabaseMutation();
  const pingSiteAgent = usePingMutation();

  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [results, setResults] = useState<RunResult[]>([]);

  const [commandLabel, setCommandLabel] = useState<string>("");

  const runBulk = async (
    commandName: string,
    command: (tenant: Tenant) => Promise<string>
  ) => {
    setIsRunning(true);
    setCommandLabel(commandName);
    setResults([]);

    const runOne = async (tenant: Tenant): Promise<RunResult> => {
      const base = { id: tenant.id, tenantName: tenant.name };
      try {
        const result = await command(tenant);
        return isOkResult(result)
          ? { ...base, ok: true }
          : { ...base, ok: false, detail: result };
      } catch (e) {
        return { ...base, ok: false, detail: describeError(e) };
      }
    };

    const results = await runInBatches(selectedTenants, runOne);

    setResults(results);

    setIsRunning(false);
  };

  const runInstallUpdate = () =>
    runBulk("Install/Update PrOS database", tenant =>
      modifyDatabase.mutateAsync({
        siteId: tenant.id,
        command: ProsDatabaseCommands.InstallUpdate
      })
    );

  const runPingSiteAgent = () =>
    runBulk("Ping Site Agent", tenant =>
      pingSiteAgent.mutateAsync({
        siteId: tenant.id,
        componentType: SiteComponentType.SiteAgent
      })
    );

  const succeeded = results.filter(result => result.ok);
  const failed = results.filter(result => !result.ok);

  return (
    <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
      <Stack
        horizontal
        verticalAlign="center"
        tokens={{ childrenGap: theme.spacing.s2 }}
      >
        <DefaultButton
          text="Ping Site Agent"
          iconProps={{ iconName: "AzureServiceEndpoint" }}
          disabled={!selectedTenants.length || isRunning}
          onClick={runPingSiteAgent}
        />
        <DefaultButton
          text="Update PrOS database"
          disabled={!selectedTenants.length || isRunning}
          onClick={runInstallUpdate}
        />

        {isRunning && <Spinner size={SpinnerSize.small} />}
      </Stack>

      {results.length > 0 && (
        <Stack tokens={{ childrenGap: theme.spacing.s2 }}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
            {commandLabel} results
          </Text>
          <Stack horizontal tokens={{ childrenGap: theme.spacing.m }}>
            <ResultColumn
              title="Succeeded"
              tone="success"
              results={succeeded}
            />
            <ResultColumn title="Failed" tone="error" results={failed} />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
